import { type RoadmapPortfolioResourceDTO, type RoadmapPortfolioResourceGroupDTO } from '@/domain/portfolio/types';
import { type RoadmapQueryParams } from '@/features/roadmap/store/filter.types';
import api, { buildURLWithParams } from '@/services/api';
import { handleException } from '@/utils/exception';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';

import type {
  BasicRoadmap,
  History,
  PlatformRelease,
  PlatformVersionDTO,
  ReleaseViewModel,
  RoadMap,
  RoadmapCategory,
  RoadmapClassification,
  RoadmapDefaultFilters,
  RoadmapFollower,
  RoadmapFundingSourceList,
  RoadmapLinkType,
  RoadmapList,
  RoadmapListDetail,
  RoadMapMinimalDTO,
  RoadmapPlatform,
  RoadmapStatus,
  RoadmapStatusItemHistoryDTO,
  RoadmapStatusResourceHistoryDTO,
  RoadmapStatusSimpleDTO,
  RoadmapStatusWithResourceDTO,
  RoadmapTempoListDetail,
  RoadmapTypeGroupDTO,
  RoadMapUpdate,
} from '../types';

const BASE_URL = 'roadmaps';

export const getRoadMaps = async (filters: RoadmapQueryParams) => {
  return await api.post<RoadmapListDetail[]>(`${BASE_URL}/filtered-roadmaps`, filters);
};

export const getRoadMapsWithTempo = async (filters: RoadmapQueryParams) => {
  return await api.post<RoadmapTempoListDetail[]>(`${BASE_URL}/filtered-roadmaps-tempo`, filters);
};

export const getRoadMapFunding = async (year: number, filterFunding: boolean) => {
  return await api.get<RoadmapFundingSourceList[]>(
    buildURLWithParams(`${BASE_URL}/funding-source/${year}`, { filterFunding }),
  );
};

export const getFollowers = async (roadmapId: number) => {
  return await api.get<RoadmapFollower[]>(`${BASE_URL}/${roadmapId}/followers`);
};

export const createFollower = async ({ idRoadmap, follower }: { idRoadmap: number; follower: RoadmapFollower }) => {
  try {
    const data = await api.post<RoadmapFollower>(`${BASE_URL}/${idRoadmap}/followers`, follower);
    toast.success('Follower added successfully');
    return data;
  } catch (e) {
    handleException(e, 'Failed to add follower');
    throw e;
  }
};

export const deleteFollower = async (id: number) => {
  try {
    await api.delete(`${BASE_URL}/followers/${id}`);
    toast.success('Follower deleted successfully');
  } catch (e) {
    handleException(e, 'Failed to delete follower');
    throw e;
  }
};

export const getStatusResources = async (roadmapId: number) => {
  return await api.get<RoadmapStatusWithResourceDTO[]>(`${BASE_URL}/${roadmapId}/status-resources`);
};

export const getRoadMapsById = async (id: number, config?: RequestInit) => {
  return await api.get<RoadMap>(`${BASE_URL}/${id}`, config);
};

export const getRoadMapMinimalById = async (id: number) => {
  return await api.get<RoadMapMinimalDTO>(`${BASE_URL}/${id}`);
};

export const getRoadmapListById = async (id: number) => {
  return await api.get<RoadmapList>(`${BASE_URL}/${id}/list`);
};

export const updateRoadMap = async (roadMap: RoadMapUpdate) => {
  await api.put(BASE_URL, roadMap);
  return roadMap;
};

export const createRoadMap = async (roadMap: RoadMapUpdate) => {
  return await api.post<RoadMap>(BASE_URL, roadMap);
};

export const deleteRoadMap = async (id: number) => {
  await api.delete(`${BASE_URL}/${id}`);
  return id;
};

export const getRoadmapPlatforms = async () => {
  return await api.get<RoadmapPlatform[]>(`${BASE_URL}/platforms`);
};

export const getRoadmapDefaultFilters = async () => {
  return await api.get<RoadmapDefaultFilters>(`${BASE_URL}/default-filters`);
};

export const getRoadMapVersions = async () => {
  return await api.get<PlatformVersionDTO[]>(`${BASE_URL}/versions`);
};

export const getRoadMapVersionsList = async () => {
  return await api.get<PlatformVersionDTO[]>(`${BASE_URL}/versions/list`);
};

export const getRoadMapReleases = async () => {
  return await api.get<PlatformRelease[]>(`${BASE_URL}/releases/list`);
};

export const getGroupedOKRs = async () => {
  return await api.get<ReleaseViewModel[]>(`${BASE_URL}/releases`);
};

export const getRoadmapsStatuses = async () => {
  return await api.get<RoadmapStatus[]>(`${BASE_URL}/statuses`);
};

export const getRoadmapsTypeStatuses = async (id: number) => {
  return await api.get<RoadmapStatus[]>(`${BASE_URL}/types/${id}/statuses`);
};

export const getRoadmapsTypeStatusesByRoadmapId = async (roadmapId: number, typeId: number, config?: RequestInit) => {
  return await api.get<RoadmapStatus[]>(`${BASE_URL}/${roadmapId}/types/${typeId}/statuses`, config);
};

export const getRoadmapsTypeStatusesByVersion = async (typeId: number, versionId: number) => {
  return await api.get<RoadmapStatus[]>(`${BASE_URL}/types/${typeId}/version/${versionId}`);
};

export const getRoadmapsHistory = async (roadmapId: number, field: string | null = null) => {
  return await api.get<History[]>(buildURLWithParams(`${BASE_URL}/${roadmapId}/history`, { field }));
};

export const getRoadmapStatusItemHistory = async (
  roadmapId: number,
  statusItemId: number,
  field: string,
  signal?: AbortSignal,
) => {
  return await api.get<RoadmapStatusItemHistoryDTO[]>(
    buildURLWithParams(`${BASE_URL}/${roadmapId}/status-item/${statusItemId}/history`, { field }),
    { signal },
  );
};

export const getRoadmapStatusResourcesHistory = async (roadmapId: number, signal?: AbortSignal) => {
  return await api.get<RoadmapStatusResourceHistoryDTO[]>(`${BASE_URL}/${roadmapId}/history/resources`, {
    signal,
  });
};

export const getRoadmapStatuses = async (roadmapId: number) => {
  return await api.get<RoadmapStatusSimpleDTO[]>(`${BASE_URL}/${roadmapId}/statuses`);
};

export const checkUserIsParticipant = async (roadmapId: number) => {
  return await api.get<boolean>(`${BASE_URL}/${roadmapId}/check-participant`);
};

export const getRoadmapClassification = async () => {
  return await api.get<RoadmapClassification[]>(`${BASE_URL}/classification`);
};

export const getRoadmapCategories = async () => {
  return await api.get<RoadmapCategory[]>(`${BASE_URL}/categories`);
};

export const getBasicRoadmap = async (filters: RoadmapQueryParams | null = null) => {
  return await api.post<BasicRoadmap[]>(`${BASE_URL}/basic`, filters);
};

export const getRoadmapTypeGroup = async (withItems = false) => {
  return await api.get<RoadmapTypeGroupDTO[]>(buildURLWithParams(`${BASE_URL}/types/group`, { withItems }));
};

export const addOrUpdateRoadmapTypeGroup = async (payload: RoadmapTypeGroupDTO[]) => {
  await api.post(`${BASE_URL}/types/group`, payload);
};

export const deleteRoadmapTypeGroup = async (id: number) => {
  await api.delete(`${BASE_URL}/types/group/${id}`);
};

export const getRoadmapResourceGroupsById = async (id: number, signal: AbortSignal) => {
  return await api.get<RoadmapPortfolioResourceGroupDTO[]>(`${BASE_URL}/${id}/resource-groups`, { signal });
};

export const getRoadmapResourcesReportById = async (id: number, signal: AbortSignal) => {
  return await api.get<RoadmapPortfolioResourceDTO[]>(`${BASE_URL}/${id}/resources`, { signal });
};

export const lockRoadmapById = async ({ idRoadmap, value }: { idRoadmap: number; value: boolean }) => {
  if (value) {
    await api.put(`${BASE_URL}/${idRoadmap}/lock`);
  } else {
    await api.put(`${BASE_URL}/${idRoadmap}/unlock`);
  }
  return idRoadmap;
};

export const getRoadmapLinkType = async () => {
  return await api.get<RoadmapLinkType[]>(`${BASE_URL}/link-type`);
};

export const exportRoadmapItems = async (roadmapIds: number[], startDate?: string, endDate?: string) => {
  const { blob, filename } = await api.downloadFile('/report/roadmaps/export-items', {
    roadmapIds,
    startDate,
    endDate,
  });
  const fallbackName = `roadmap_export_${new Date().toISOString().replace(/[:.]/g, '-')}.xlsx`;
  saveAs(blob, filename || fallbackName);
};
